<script setup lang="ts">
import type { DownloadTreeNode } from '~/types/download'
import { faAngleDown, faAngleRight } from '@fortawesome/pro-light-svg-icons'
import Checkbox from '~/components/form/Checkbox.vue'
import { useDescendants } from '~/composables/download/useDescendants'
import { useDownloadItems } from '~/composables/download/useDownloadItems'
import { useDownloadNode } from '~/composables/download/useDownloadNode'
import DownloadTooltip from './Tooltip.vue'

const props = withDefaults(defineProps<{
  tocItem: DownloadTreeNode
  selectedId?: string
  fallbackLabel?: string
  fallbackIndex?: number
  noAvailabilityLabel?: string
  renderLeafNodes?: boolean
  level?: number
  isBookDownload?: boolean
}>(), {
  renderLeafNodes: true,
  level: 0,
  isBookDownload: false,
})

const { checkedItems, onToggleChecked, onToggleCheckedBulk } = useDownloadItems()

const showChildren = ref(true)

const { descendantIds } = useDescendants(props.tocItem, props.renderLeafNodes)
const { isUnavailable, children, hasRenderableChildren, shouldRenderNode, availabilityAppendix } = useDownloadNode(props.tocItem, props.level, props.renderLeafNodes, props.noAvailabilityLabel)

const fallbackLabelText = computed(() => props.fallbackLabel ?? 'Del')
const hasParent = computed(() => (props.level ?? 0) > 0)

function selectionKey(node?: DownloadTreeNode | null) {
  if (!node) {
    return null
  }
  return node.downloadId?.toLocaleLowerCase?.() ?? node.id?.toLocaleLowerCase?.() ?? null
}

function hasSelection(node?: DownloadTreeNode | null) {
  const key = selectionKey(node)
  if (!key) {
    return false
  }
  return checkedItems.value.has(key)
}

const childrenAllSelected = computed(() => {
  if (!descendantIds.value.length) {
    return false
  }

  return descendantIds.value.every(node => hasSelection(node))
})

const childrenAllDeselected = computed(() => {
  if (!descendantIds.value.length) {
    return false
  }
  return descendantIds.value.every(node => !hasSelection(node))
})

function isChecked() {
  return hasSelection(props.tocItem)
}

function toggleChecked(checked: boolean) {
  if (!props.tocItem.id) {
    return
  }
  onToggleChecked({ checked, item: props.tocItem })
}

function onToggleNode(checked: boolean) {
  if (props.isBookDownload && hasRenderableChildren.value) {
    toggleAllDescendants(checked)
    return
  }

  toggleChecked(checked)
}

function toggleAllDescendants(checked: boolean) {
  const items: DownloadTreeNode[] = []
  // Include self unless explicitly unavailable
  if (props.tocItem.id && !isUnavailable.value) {
    items.push(props.tocItem)
  }
  descendantIds.value.forEach(id => items.push(id))
  onToggleCheckedBulk({ items, checked })
}

function onMarkAll() {
  toggleAllDescendants(true)
}

function onRemoveAll() {
  toggleAllDescendants(false)
}
</script>

<template>
  <li v-if="shouldRenderNode" :id="`toc-${tocItem.id}`" class="flex flex-col items-start scroll-mt-4xl gap-3xs">
    <div class="flex text-center gap-3xs items-center">
      <ControlButton
        v-if="hasRenderableChildren"
        :id="tocItem.id"
        :icon="showChildren ? faAngleDown : faAngleRight"
        class="flex items-center rounded-lg hover:bg-surface-hover focus:outline-none focus:ring-blurple-300"
        :class="{ 'bg-surface-primary': showChildren }" aria-label="Vis eller skjul underpunkter"
        @click="showChildren = !showChildren"
      />
      <div v-else-if="!hasParent" class="w-8 h-8 px-2xs mt-0.5 invisible">
        <Icon :icon="showChildren ? faAngleDown : faAngleRight" />
      </div>
      <div class="flex w-full items-center text-left px-3xs rounded-lg gap-3xs" :class="{ 'ml-7': !hasRenderableChildren && hasParent }">
        <Checkbox
          class="shrink-0"
          :model-value="isChecked()"
          :disabled="isUnavailable"
          :aria-label="tocItem.title || `${fallbackLabelText} ${fallbackIndex}`"
          @update:model-value="(checked: boolean) => onToggleNode(checked)"
        >
          <template #label>
            <button
              class="flex items-center ml-[-0.5rem] p-2xs text-left rounded-lg hover:bg-surface-hover focus:outline-none focus:ring-blurple-300"
              :class="{ 'text-secondary text-sm': isUnavailable, 'text-small reading-dark:text-carbon-50': !isUnavailable }"
              :disabled="isUnavailable" :aria-label="tocItem.title || `${fallbackLabelText} ${fallbackIndex}`"
              @click="onToggleNode(!isChecked())"
            >
              {{ tocItem.title }}{{ availabilityAppendix }}
            </button>
          </template>
        </Checkbox>
        <DownloadTooltip
          v-if="hasRenderableChildren && !isBookDownload" :content="tocItem.title" :disable-mark-all="childrenAllSelected"
          :disable-remove-all="childrenAllDeselected" @mark-all="onMarkAll" @remove-all="onRemoveAll"
        />
      </div>
    </div>

    <ul
      v-if="showChildren && hasRenderableChildren" class="flex flex-col pl-l gap-3xs" :aria-expanded="showChildren"
      :aria-label="tocItem.title || undefined"
    >
      <DownloadItem
        v-for="(child, i) in children"
        :key="child.id"
        :toc-item="child"
        :fallback-index="i + 1"
        :fallback-label="fallbackLabelText"
        :selected-id="selectedId"
        :checked-items="checkedItems"
        :render-leaf-nodes="renderLeafNodes"
        :no-availability-label="noAvailabilityLabel"
        :level="(props.level ?? 0) + 1"
        :is-book-download="isBookDownload"
      />
    </ul>
  </li>
</template>
